/**
 * Track Attribution (client)
 *
 * Captures Google Ads click-IDs (gclid/gbraid/wbraid), UTM tags, and the
 * external referrer on initial landing AND on every subsequent route
 * navigation, persisting them in the `metwide_attr` first-party cookie
 * (90-day, Lax, Secure).
 *
 * Last-touch wins, but only for keys present in the current capture
 * (URL, linker cookies, or document.referrer). Navigating to a sibling
 * page without query params does NOT clear a previously captured gclid;
 * a same-origin or empty `document.referrer` does NOT clear a previously
 * captured referrer. This matches Google's click-ID attribution default
 * and protects multi-page browse sessions.
 *
 * Reads `window.location.search` directly (not GTM dataLayer) so the
 * cookie is written even if a fast user submits before tag-managed
 * scripts finish loading. As a fallback it also reads Google's
 * Conversion Linker cookies (`_gcl_aw` / `_gcl_gb`) so a click ID is
 * still recovered if it never reached the URL we saw but gtag.js
 * captured it from the original navigation; these only ever *fill* a
 * click ID we don't already hold, never override one.
 *
 * `document.referrer` is filtered to cross-origin values before storage
 * (see `readReferrer`) so internal navigations cannot overwrite the
 * true external referrer.
 */
import {
  pickAttributionFields,
  readAttributionFromQuery,
  readClickIdsFromGclCookies,
  readGa4IdsFromCookies,
  readReferrer,
  type LeadAttributionKey,
  type LeadAttributionPayload,
} from '~~/shared/lead-attribution'
import { useAttributionCookie } from '~/composables/useAttribution'

export default defineNuxtPlugin(() => {
  const router = useRouter()
  const cookie = useAttributionCookie()
  const runtimeConfig = useRuntimeConfig()
  // Public runtime config carries the GA4 measurement id used to build
  // the per-property `_ga_<measurement-id>` cookie name. Distinct from
  // `gtmId` (GTM container id, shape `GTM-XXXXXXX`): the GA4 session
  // cookie is keyed by the GA4 measurement id (shape `G-XXXXXXXXXX`)
  // with the `G-` prefix stripped. If unset (local dev without GA4),
  // readGa4IdsFromCookies silently skips the session-id read.
  const ga4MeasurementId = String(runtimeConfig.public.ga4MeasurementId ?? '')

  function capture() {
    const fromUrl = readAttributionFromQuery(window.location.search)
    const fromGclCookies = readClickIdsFromGclCookies(document.cookie)
    const fromGa4Cookies = readGa4IdsFromCookies(document.cookie, ga4MeasurementId)
    const fromReferrer = readReferrer(
      document.referrer,
      window.location.origin,
    )
    if (
      Object.keys(fromUrl).length === 0 &&
      Object.keys(fromGclCookies).length === 0 &&
      Object.keys(fromGa4Cookies).length === 0 &&
      Object.keys(fromReferrer).length === 0
    ) {
      return
    }

    const merged: LeadAttributionPayload = pickAttributionFields(
      cookie.value as Record<string, unknown> | null | undefined,
    )

    // Linker-cookie fallback: only fill click IDs we don't already have.
    for (const [key, value] of Object.entries(fromGclCookies)) {
      if (merged[key as LeadAttributionKey] == null) {
        merged[key as LeadAttributionKey] = value
      }
    }

    // GA4 identifiers: client id is sticky across sessions so refresh
    // it whenever a read returns one (Object.assign below); session id
    // rolls over every 30 minutes of inactivity, so always overwrite
    // when a fresh read returns one. Object.assign covers both because
    // readGa4IdsFromCookies omits keys it could not parse.
    Object.assign(merged, fromGa4Cookies)

    // URL and referrer are the freshest signals captured this tick:
    // they win last-touch over anything carried in from the cookie.
    Object.assign(merged, fromUrl, fromReferrer)

    cookie.value = merged
  }

  // Initial landing: covers direct entry with `?gclid=...` or arriving
  // from an external site (document.referrer set).
  capture()

  // SPA navigations: the user may arrive at a deep link with query
  // params after the plugin has already mounted (external referer
  // into a route the SPA already cached), or gtag.js may only have
  // written `_gcl_aw` after the first paint. document.referrer is
  // stable across SPA navigations, so a same-origin re-capture is
  // filtered out by `readReferrer` and cannot clobber the original.
  router.afterEach(() => {
    capture()
  })
})
